/**
 * Geometry utilities
 * Per PLAN.md utils/geometry.js
 */

/**
 * Calculate distance between two points
 * @param {number} x1 
 * @param {number} y1 
 * @param {number} x2 
 * @param {number} y2 
 * @returns {number}
 */
export function distance(x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Check if point is inside rectangle
 * @param {number} px 
 * @param {number} py 
 * @param {number} rx 
 * @param {number} ry 
 * @param {number} rw 
 * @param {number} rh 
 * @returns {boolean}
 */
export function pointInRect(px, py, rx, ry, rw, rh) {
    return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

/**
 * Check if two rectangles overlap
 * @param {{x: number, y: number, width: number, height: number}} a 
 * @param {{x: number, y: number, width: number, height: number}} b 
 * @returns {boolean}
 */
export function rectsOverlap(a, b) {
    return !(
        a.x + a.width < b.x ||
        b.x + b.width < a.x ||
        a.y + a.height < b.y ||
        b.y + b.height < a.y
    );
}

/**
 * Get closest point on rectangle edge to a point
 * @param {number} px 
 * @param {number} py 
 * @param {{x: number, y: number, width: number, height: number}} rect 
 * @returns {{x: number, y: number}}
 */
export function closestPointOnRectEdge(px, py, rect) {
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;

    const dx = px - cx;
    const dy = py - cy;

    if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
        return { x: rect.x + rect.width, y: cy };
    }

    const halfWidth = rect.width / 2;
    const halfHeight = rect.height / 2;

    // Calculate intersection with each edge
    const scale = Math.min(
        halfWidth / Math.abs(dx || 0.001),
        halfHeight / Math.abs(dy || 0.001)
    );

    return {
        x: cx + dx * scale,
        y: cy + dy * scale
    };
}

/**
 * Calculate bezier curve control points for curved connection
 * @param {number} x1 - Start X
 * @param {number} y1 - Start Y
 * @param {number} x2 - End X
 * @param {number} y2 - End Y
 * @param {number} curvature - Curvature amount (0 = straight)
 * @returns {{cp1x: number, cp1y: number, cp2x: number, cp2y: number}}
 */
export function calculateBezierControlPoints(x1, y1, x2, y2, curvature = 0) {
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;

    // Perpendicular offset
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    const perpX = -dy / len * curvature;
    const perpY = dx / len * curvature;

    return {
        cp1x: midX + perpX,
        cp1y: midY + perpY,
        cp2x: midX + perpX,
        cp2y: midY + perpY
    };
}

/**
 * Get the angle between two points in radians
 * @param {number} x1 
 * @param {number} y1 
 * @param {number} x2 
 * @param {number} y2 
 * @returns {number}
 */
export function angle(x1, y1, x2, y2) {
    return Math.atan2(y2 - y1, x2 - x1);
}

/**
 * Clamp a value between min and max
 * @param {number} value 
 * @param {number} min 
 * @param {number} max 
 * @returns {number}
 */
export function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

/**
 * Linear interpolation
 * @param {number} a 
 * @param {number} b 
 * @param {number} t 
 * @returns {number}
 */
export function lerp(a, b, t) {
    return a + (b - a) * t;
}

/**
 * Snap value to grid
 * @param {number} value 
 * @param {number} gridSize 
 * @returns {number}
 */
export function snapToGrid(value, gridSize) {
    return Math.round(value / gridSize) * gridSize;
}
